// ===================================================================
//  Photos page — nav behavior, masonry wall, and Flip lightbox
// ===================================================================

gsap.registerPlugin(Flip);

gsap.defaults({
  ease: "power4.inOut",
  duration: 0.8,
});

// -------------------- Navbar (sticky + hamburger) --------------------
// Mirrors the behavior in index.js so the shared navbar feels identical.
function initNavbar() {
  const navbar = document.getElementById("navbar");
  const spacer = document.getElementById("nav-spacer");
  if (!navbar || !spacer) return;

  window.addEventListener(
    "scroll",
    function () {
      if (!navbar.classList.contains("sticky")) {
        if (window.scrollY > 800) {
          // Reserve the navbar's in-flow height before it goes fixed so the
          // page doesn't collapse upward into the gap it leaves behind.
          spacer.style.height = navbar.offsetHeight + "px";
          navbar.classList.add("sticky");
        }
      } else if (window.scrollY < 300) {
        navbar.classList.remove("sticky");
        navbar.classList.add("sticky-gone");
      }

      if (navbar.classList.contains("sticky-gone") && window.scrollY < 150) {
        navbar.classList.remove("sticky-gone");
        spacer.style.height = "0px";
      }
    },
    { passive: true },
  );

  const toggleButton = document.getElementsByClassName("nav-toggle-button")[0];
  const navbarLinks = document.getElementsByClassName("nav-links")[0];
  if (toggleButton && navbarLinks) {
    toggleButton.addEventListener("click", () => {
      navbarLinks.classList.toggle("active");
      navbar.classList.toggle("nav-active");
    });
  }
}

// -------------------- Button Character Stagger --------------------
function initButtonCharacterStagger() {
  const offsetIncrement = 0.01;
  const buttons = document.querySelectorAll("[data-button-animate-chars]");

  buttons.forEach((button) => {
    const text = button.textContent;
    button.innerHTML = "";

    [...text].forEach((char, index) => {
      const span = document.createElement("span");
      span.textContent = char;
      span.style.transitionDelay = `${index * offsetIncrement}s`;
      if (char === " ") span.style.whiteSpace = "pre";
      button.appendChild(span);
    });
  });
}

// -------------------- Masonry Grid --------------------
function initMasonryGrid() {
  document.querySelectorAll("[data-masonry-list]").forEach((container) => {
    const shuffle = container.dataset.masonryShuffle !== "false";
    let cols, gapPx, colHeights;

    // Take columns and gaps from CSS
    const getVars = () => {
      const cs = getComputedStyle(container);
      cols = parseInt(cs.getPropertyValue("--masonry-col"));
      const rawGap = cs.getPropertyValue("--masonry-gap").trim();
      if (rawGap.endsWith("px")) {
        gapPx = parseFloat(rawGap);
      } else if (rawGap.endsWith("em")) {
        gapPx = parseFloat(rawGap) * parseFloat(cs.fontSize);
      } else if (rawGap.endsWith("rem")) {
        gapPx =
          parseFloat(rawGap) *
          parseFloat(getComputedStyle(document.documentElement).fontSize);
      } else {
        gapPx = parseFloat(rawGap);
      }
    };

    // Set the layout
    const layout = () => {
      getVars();
      const wCalc = `(100% - ${cols - 1}*var(--masonry-gap)) / ${cols}`;
      colHeights = Array(cols).fill(0);
      container.style.position = "relative";
      const items = Array.from(container.children);
      items.forEach((el) => {
        el.style.position = "absolute";
        el.style.width = `calc(${wCalc})`;
      });
      items.forEach((el, i) => {
        const h = el.offsetHeight;
        const idx = shuffle
          ? colHeights.indexOf(Math.min(...colHeights))
          : i % cols;
        el.style.top = `${colHeights[idx]}px`;
        el.style.left = `calc(${wCalc}*${idx} + var(--masonry-gap)*${idx})`;
        colHeights[idx] += h + gapPx;
      });
      container.style.height = `${Math.max(...colHeights)}px`;
    };

    // Debounce function
    const debounce = (fn, delay) => {
      let t;
      return () => {
        clearTimeout(t);
        t = setTimeout(fn, delay);
      };
    };

    // Resize handler
    const onResize = debounce(layout, 100);
    window.addEventListener("resize", onResize);

    // Watch for image loads (fallback if no aspect-ratio defined)
    const debouncedLayout = debounce(layout, 50);
    const imgLoad = () => {
      container.querySelectorAll("img").forEach((img) => {
        if (!img.complete) {
          img.addEventListener("load", debouncedLayout, { once: true });
          img.addEventListener("error", debouncedLayout, { once: true });
        }
      });
    };

    // Run layout immediately, then watch for straggler images
    layout();
    imgLoad();

    // Constructor with destroy and recalc function
    container._masonry = {
      recalc: () => {
        layout();
        imgLoad();
      },
      destroy: () => {
        window.removeEventListener("resize", onResize);
        const items = Array.from(container.children);
        items.forEach((el) => {
          el.style.position = el.style.width = el.style.top = el.style.left = "";
        });
        container.style.position = container.style.height = "";
      },
    };
  });
}

// -------------------- Lightbox (GSAP Flip) --------------------
// Build the parallel [data-lightbox="item"] list from the grid images so the
// markup stays a single source of truth.
function buildLightboxItems(container) {
  const list = container.querySelector(".lightbox-img__list");
  if (!list) return;
  const gridImgs = container.querySelectorAll('[data-lightbox="trigger"] img');
  gridImgs.forEach((img) => {
    const item = document.createElement("div");
    item.setAttribute("data-lightbox", "item");
    item.className = "lightbox-img__item";
    const clone = document.createElement("img");
    clone.src = img.currentSrc || img.src;
    clone.alt = img.alt || "";
    clone.loading = "lazy";
    clone.className = "lightbox-img";
    item.appendChild(clone);
    list.appendChild(item);
  });
}

function createLightbox(
  container,
  { onStart, onOpen, onClose, onCloseComplete } = {},
) {
  const elements = {
    wrapper: container.querySelector('[data-lightbox="wrapper"]'),
    triggers: container.querySelectorAll('[data-lightbox="trigger"]'),
    triggerParents: container.querySelectorAll('[data-lightbox="trigger-parent"]'),
    items: container.querySelectorAll('[data-lightbox="item"]'),
    nav: container.querySelectorAll('[data-lightbox="nav"]'),
    counter: {
      current: container.querySelector('[data-lightbox="counter-current"]'),
      total: container.querySelector('[data-lightbox="counter-total"]'),
    },
    buttons: {
      prev: container.querySelector('[data-lightbox="prev"]'),
      next: container.querySelector('[data-lightbox="next"]'),
      close: container.querySelector('[data-lightbox="close"]'),
    },
  };

  // Create our main timeline that will coordinate all animations
  const mainTimeline = gsap.timeline();

  // ————————— COUNTER ————————— //
  if (elements.counter.total) {
    elements.counter.total.textContent = elements.triggers.length;
  }

  // ————————— CLOSE FUNCTION ————————— //
  function closeLightbox() {
    onClose?.();

    // First, we clear any running animations to prevent conflicts
    mainTimeline.clear();
    gsap.killTweensOf([
      elements.wrapper,
      elements.nav,
      elements.triggerParents,
      elements.items,
      container.querySelector('[data-lightbox="original"]'),
    ]);

    const tl = gsap.timeline({
      defaults: { ease: "power2.inOut" },
      onComplete: () => {
        elements.wrapper.classList.remove("is-active");

        // Show all hidden images in lightbox items
        elements.items.forEach((item) => {
          item.classList.remove("is-active");
          const lightboxImage = item.querySelector("img");
          if (lightboxImage) {
            lightboxImage.style.display = "";
          }
        });

        // Clear any lingering transform properties on the original image
        const originalImg = container.querySelector('[data-lightbox="original"]');
        if (originalImg) {
          gsap.set(originalImg, { clearProps: "all" });
        }

        // Remove the fixed height from the trigger parent
        const originalParent = container.querySelector('[data-lightbox="original-parent"]');
        if (originalParent) {
          originalParent.parentElement.style.removeProperty("height");
        }

        onCloseComplete?.();
      },
    });

    // First, find and move back the original item
    const originalItem = container.querySelector('[data-lightbox="original"]');
    const originalParent = container.querySelector('[data-lightbox="original-parent"]');

    if (originalItem && originalParent) {
      gsap.set(originalItem, { clearProps: "all" });
      originalParent.appendChild(originalItem);
      originalParent.removeAttribute("data-lightbox");
      originalItem.removeAttribute("data-lightbox");
    }

    // Find active slide
    let activeLightboxSlide = container.querySelector('[data-lightbox="item"].is-active');

    // Return animation
    tl.to(elements.triggerParents, {
      autoAlpha: 1,
      duration: 0.5,
      stagger: 0.03,
      overwrite: true,
    })
      .to(
        elements.nav,
        {
          autoAlpha: 0,
          y: "1rem",
          duration: 0.4,
          stagger: 0,
        },
        "<",
      )
      .to(
        elements.wrapper,
        {
          backgroundColor: "rgba(0,0,0,0)",
          duration: 0.4,
        },
        "<",
      )
      .to(
        activeLightboxSlide,
        {
          autoAlpha: 0,
          duration: 0.4,
        },
        "<",
      )
      .set([elements.items, activeLightboxSlide, elements.triggerParents], {
        clearProps: "all",
      });

    mainTimeline.add(tl);
  }

  // ————————— CLICK-OUTSIDE FUNCTIONALITY ————————— //
  function handleOutsideClick(event) {
    if (event.detail === 0) {
      return;
    }

    const clickedElement = event.target;
    const isOutside = !clickedElement.closest(
      '[data-lightbox="item"].is-active img, [data-lightbox="nav"], [data-lightbox="close"], [data-lightbox="trigger"]',
    );

    if (isOutside) {
      closeLightbox();
    }
  }

  // ————————— TOGGLE ACTIVE ITEM IN LIGHTBOX ————————— //
  function updateActiveItem(index) {
    elements.items.forEach((item) => item.classList.remove("is-active"));
    elements.items[index].classList.add("is-active");

    if (elements.counter.current) {
      elements.counter.current.textContent = index + 1;
    }
  }

  // ————————— CLICK TO OPEN ————————— //
  elements.triggers.forEach((trigger, index) => {
    trigger.addEventListener("click", () => {
      onStart?.();

      mainTimeline.clear();
      gsap.killTweensOf([elements.wrapper, elements.nav, elements.triggerParents]);

      const img = trigger.querySelector("img");
      const state = Flip.getState(img);

      // Store the trigger's current height before the FLIP animation
      // So the grid does not collapse
      const triggerRect = trigger.getBoundingClientRect();
      trigger.parentElement.style.height = `${triggerRect.height}px`;

      // Save element and parent that was clicked
      trigger.setAttribute("data-lightbox", "original-parent");
      img.setAttribute("data-lightbox", "original");

      // Set correct lightbox item to visible
      updateActiveItem(index);

      // Start listening for clicks outside of lightbox
      container.addEventListener("click", handleOutsideClick);

      const tl = gsap.timeline({
        onComplete: () => {
          onOpen?.();
        },
      });
      elements.wrapper.classList.add("is-active");
      const targetItem = elements.items[index];

      // Hide the original image in the lightbox item
      const lightboxImage = targetItem.querySelector("img");
      if (lightboxImage) {
        lightboxImage.style.display = "none";
      }

      // Fade out other grid items
      elements.triggerParents.forEach((otherTrigger) => {
        if (otherTrigger !== trigger) {
          gsap.to(otherTrigger, {
            autoAlpha: 0,
            duration: 0.4,
            stagger: 0.02,
            overwrite: true,
          });
        }
      });

      // Flip clicked image into lightbox
      if (!targetItem.contains(img)) {
        targetItem.appendChild(img);
        tl.add(
          Flip.from(state, {
            targets: img,
            absolute: true,
            duration: 0.6,
            ease: "power2.inOut",
          }),
          0,
        );
      }

      // Animate in our navigation and background
      tl.to(
        elements.wrapper,
        {
          backgroundColor: "rgba(0,0,0,0.75)",
          duration: 0.6,
        },
        0,
      ).fromTo(
        elements.nav,
        {
          autoAlpha: 0,
          y: "1rem",
        },
        {
          autoAlpha: 1,
          y: "0rem",
          duration: 0.6,
          stagger: { each: 0.05, from: "center" },
        },
        0.2,
      );

      mainTimeline.add(tl);
    });
  });

  // ————————— NAV BUTTONS ————————— //
  if (elements.buttons.next) {
    elements.buttons.next.addEventListener("click", () => {
      const currentIndex = Array.from(elements.items).findIndex((item) =>
        item.classList.contains("is-active"),
      );
      const nextIndex = (currentIndex + 1) % elements.items.length;
      updateActiveItem(nextIndex);
    });
  }

  if (elements.buttons.prev) {
    elements.buttons.prev.addEventListener("click", () => {
      const currentIndex = Array.from(elements.items).findIndex((item) =>
        item.classList.contains("is-active"),
      );
      const prevIndex =
        (currentIndex - 1 + elements.items.length) % elements.items.length;
      updateActiveItem(prevIndex);
    });
  }

  if (elements.buttons.close) {
    elements.buttons.close.addEventListener("click", closeLightbox);
  }

  // ————————— KEYBOARD NAV ————————— //
  document.addEventListener("keydown", (event) => {
    if (!elements.wrapper.classList.contains("is-active")) return;
    switch (event.key) {
      case "Escape":
        closeLightbox();
        break;
      case "ArrowRight":
        elements.buttons.next?.click();
        break;
      case "ArrowLeft":
        elements.buttons.prev?.click();
        break;
    }
  });
}

// -------------------- Init --------------------
document.addEventListener("DOMContentLoaded", () => {
  initNavbar();
  initButtonCharacterStagger();
  initMasonryGrid();

  document.querySelectorAll("[data-gallery]").forEach((wrapper) => {
    buildLightboxItems(wrapper);
    const masonryList = wrapper.querySelector("[data-masonry-list]");
    createLightbox(wrapper, {
      // The lightbox lifts the clicked <img> out with GSAP Flip and drops it back
      // on close. Before re-running the masonry layout, scrub any residual inline
      // sizing the Flip/lightbox may have left on the items and images, then
      // measure on the next frame once the browser has reflowed the re-inserted
      // image — otherwise a natural-aspect tile can be measured too early and the
      // wall ends up with a gap.
      onCloseComplete: () => {
        if (!masonryList) return;
        masonryList
          .querySelectorAll(".masonry-item__inner")
          .forEach((el) => el.style.removeProperty("height"));
        masonryList
          .querySelectorAll(".masonry-item__visual-img")
          .forEach((img) => img.removeAttribute("style"));
        requestAnimationFrame(() =>
          requestAnimationFrame(() => masonryList._masonry?.recalc()),
        );
      },
    });
  });
});
